/**
 * BoardSelectorModal - Multi-Board 3D Cribbage Gallery & Custom Board Manager
 * Allows players to browse 3D boards, toggle between 2D and 3D mode,
 * and manage (Add, Edit, Rename, Delete) custom 3D boards saved in the player profile per brand.
 */

import { Board3DCatalog } from '../board/Board3DCatalog.js';
import { SettingsManager } from './SettingsManager.js';
import { Profile, GAME_BRANDS } from '../profile/Profile.js';

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  // Allow safe protocols (http, https, data:image, or relative paths)
  if (/^(?:https?:\/\/|\/|\.\/|\.\.\/|data:image\/)/i.test(trimmed)) {
    return trimmed.replace(/[<>"'`]/g, '');
  }
  return '';
}

export class BoardSelectorModal {
  /**
   * Opens the 3D Board Gallery modal.
   * @param {Object} options
   */
  static show(options = {}) {
    const initialBrand = options.brand || SettingsManager.getAppBrand() || 'crazy';
    const settings = options.settings || (typeof window !== 'undefined' ? window.settings : {}) || {};
    const onSelect = options.onSelect || (() => {});

    let currentBrandTab = initialBrand;

    let modalEl = document.getElementById('board3DModal');
    if (!modalEl) {
      modalEl = document.createElement('div');
      modalEl.id = 'board3DModal';
      modalEl.className = 'board-3d-overlay';
      modalEl.setAttribute('role', 'dialog');
      modalEl.setAttribute('aria-modal', 'true');
      modalEl.setAttribute('aria-labelledby', 'board3DModalTitle');
      document.body.appendChild(modalEl);
    }

    // Fullscreen / 3D Room / Modal Container Reparenting & Stacking Context
    const fsElem = (typeof document !== 'undefined')
      ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
      : null;
    const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
      ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
      : null;
    const activeModalOverlay = document.getElementById('overlay');
    const modalParent = (activeModalOverlay && activeModalOverlay.parentElement && activeModalOverlay.parentElement !== document.body)
      ? activeModalOverlay.parentElement
      : null;
    const b3dContainer = document.getElementById('board3DContainer');
    const is3DFullscreen = Boolean(
      fsElem || b3dElem || (b3dContainer && (b3dContainer.classList?.contains?.('is-fullscreen') || (typeof document !== 'undefined' && document.body?.classList?.contains?.('is-3d-fullscreen'))))
    );

    const targetParent = fsElem || b3dElem || modalParent || (is3DFullscreen ? b3dContainer : null);
    if (targetParent && modalEl.parentElement !== targetParent) {
      targetParent.appendChild(modalEl);
    } else if (!targetParent && modalEl.parentElement !== document.body && document.body) {
      document.body.appendChild(modalEl);
    }

    modalEl.style.cssText = 'position: fixed; inset: 0; top: 0; left: 0; right: 0; bottom: 0; width: 100vw; height: 100vh; background: rgba(0, 0, 0, 0.84); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 20000000; padding: 14px; box-sizing: border-box;';
    modalEl.style.zIndex = '20000000';

    const getBrandBadge = (brandVal) => {
      if (!brandVal || brandVal === 'all') {
        return '<span style="font-size: 0.68rem; padding: 2px 6px; border-radius: 4px; background: rgba(56, 189, 248, 0.2); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.4); font-weight: 700;">🌐 All Brands</span>';
      }
      const b = GAME_BRANDS.find(g => g.id === brandVal);
      if (b) {
        return `<span style="font-size: 0.68rem; padding: 2px 6px; border-radius: 4px; background: ${b.badgeBg}; color: ${b.color}; border: 1px solid ${b.border}; font-weight: 700;">${b.icon} ${b.shortName}</span>`;
      }
      return `<span style="font-size: 0.68rem; padding: 2px 6px; border-radius: 4px; background: rgba(255,255,255,0.1); color: #cbd5e1; border: 1px solid rgba(255,255,255,0.2); font-weight: 700;">${escapeHtml(brandVal)}</span>`;
    };

    const renderModal = () => {
      const allBrandBoards = Board3DCatalog.getBoards(currentBrandTab);
      const officialBoards = allBrandBoards.filter(b => !b.isCustom);

      const selectedId = officialBoards.some(b => b.id === settings.selected3DBoardId)
        ? settings.selected3DBoardId
        : (officialBoards[0]?.id || (currentBrandTab === 'clash' ? 'board-cribbage-clash' : 'board-14'));
      const is3DActive = settings.boardViewMode === '3d';

      modalEl.innerHTML = `
        <div class="modal-card board-3d-modal-card" style="box-sizing: border-box; background: linear-gradient(135deg, rgba(22, 16, 8, 0.98), rgba(38, 26, 12, 0.98)); border: 1.5px solid rgba(240, 198, 74, 0.5); border-radius: 18px; box-shadow: 0 16px 45px rgba(0, 0, 0, 0.8), 0 0 25px rgba(240, 198, 74, 0.2); max-width: 860px; width: 95vw; max-height: 92vh; display: flex; flex-direction: column; overflow: hidden;">
          <div class="modal-header" style="display: flex; justify-content: space-between; align-items: center; padding: 16px 22px; border-bottom: 1px solid rgba(240, 198, 74, 0.25); background: rgba(0,0,0,0.35);">
            <div style="display: flex; align-items: center; gap: 12px;">
              <span style="font-size: 1.7rem;">🎲</span>
              <div>
                <h2 id="board3DModalTitle" style="margin: 0; font-size: 1.35rem; color: var(--gold, #f0c64a); font-weight: 800; letter-spacing: 0.03em;">3D Cribbage Boards</h2>
                <div style="font-size: 0.82rem; color: #cbd5e1;">Browse official 3D cribbage boards across all game brands</div>
              </div>
            </div>
            <button type="button" class="btn-ghost" id="btnBoard3DClose" aria-label="Close modal" style="font-size: 1.4rem; color: #f0c64a; cursor: pointer; padding: 4px 10px; border-radius: 8px; border: none; background: transparent; line-height: 1;">✕</button>
          </div>

          <div class="modal-body" style="padding: 16px 22px; max-height: calc(92vh - 140px); overflow-y: auto;">
            <!-- View Mode Toggle -->
            <div style="display: flex; align-items: center; justify-content: space-between; background: rgba(0,0,0,0.35); padding: 12px 16px; border-radius: 12px; margin-bottom: 14px; border: 1px solid rgba(240,198,74,0.25); flex-wrap: wrap; gap: 10px;">
              <div>
                <div style="font-weight: 700; color: #fff;">Render Mode</div>
                <div style="font-size: 0.8rem; color: #94a3b8;">Choose between classic 2D SVG or interactive 3D WebGL</div>
              </div>
              <div style="display: inline-flex; gap: 8px;">
                <button type="button" class="btn-toggle-view ${!is3DActive ? 'active' : ''}" id="btnMode2D" style="padding: 7px 16px; border-radius: 8px; font-weight: 700; font-size: 0.85rem; cursor: pointer; border: 1px solid ${!is3DActive ? 'var(--gold, #f0c64a)' : 'rgba(255,255,255,0.2)'}; background: ${!is3DActive ? 'var(--gold, #f0c64a)' : 'transparent'}; color: ${!is3DActive ? '#000' : '#cbd5e1'};">2D Classic</button>
                <button type="button" class="btn-toggle-view ${is3DActive ? 'active' : ''}" id="btnMode3D" style="padding: 7px 16px; border-radius: 8px; font-weight: 700; font-size: 0.85rem; cursor: pointer; border: 1px solid ${is3DActive ? 'var(--gold, #f0c64a)' : 'rgba(255,255,255,0.2)'}; background: ${is3DActive ? 'var(--gold, #f0c64a)' : 'transparent'}; color: ${is3DActive ? '#000' : '#cbd5e1'};">3D Interactive</button>
              </div>
            </div>

            <!-- Brand Selection Navigation Tabs -->
            <div style="display: flex; gap: 6px; margin-bottom: 16px; overflow-x: auto; padding-bottom: 4px; border-bottom: 1px solid rgba(240,198,74,0.18);">
              <button type="button" class="board-brand-tab-btn ${currentBrandTab === 'all' ? 'active' : ''}" data-brand-tab="all" style="padding: 6px 12px; border-radius: 8px; font-size: 0.82rem; font-weight: 700; cursor: pointer; white-space: nowrap; border: 1px solid ${currentBrandTab === 'all' ? '#f59e0b' : 'rgba(244,234,212,0.18)'}; background: ${currentBrandTab === 'all' ? 'rgba(245,158,11,0.25)' : 'rgba(0,0,0,0.3)'}; color: ${currentBrandTab === 'all' ? '#facc15' : '#d1d5db'};">
                🌐 All Brands
              </button>
              ${GAME_BRANDS.map(b => {
                const isAct = currentBrandTab === b.id;
                return `
                  <button type="button" class="board-brand-tab-btn ${isAct ? 'active' : ''}" data-brand-tab="${b.id}" style="padding: 6px 12px; border-radius: 8px; font-size: 0.82rem; font-weight: 700; cursor: pointer; white-space: nowrap; border: 1px solid ${isAct ? b.color : 'rgba(244,234,212,0.18)'}; background: ${isAct ? b.badgeBg : 'rgba(0,0,0,0.3)'}; color: ${isAct ? '#fff' : '#d1d5db'}; display: inline-flex; align-items: center; gap: 5px;">
                    <span>${b.icon} ${b.shortName}</span>
                  </button>
                `;
              }).join('')}
            </div>

            <!-- Official 3D Boards for the Brand -->
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;">
              <span style="font-weight: 800; font-size: 1.05rem; color: #f4ead4;">🏛️ Official 3D Boards (${officialBoards.length})</span>
            </div>

            <div class="board-3d-grid" id="official3DGrid">
              ${officialBoards.map(b => {
                const isSelected = b.id === selectedId;
                const safeId = escapeHtml(b.id);
                const safeName = escapeHtml(b.name);
                const safeDesc = escapeHtml(b.desc || 'Standard 3D Cribbage Board');
                const safeThumb = sanitizeUrl(b.thumbnailUrl);
                return `
                  <div class="board-3d-card ${isSelected ? 'is-selected' : ''}" data-board-id="${safeId}" tabindex="0" role="button" aria-label="Select ${safeName}">
                    <div>
                      ${safeThumb ? `
                        <div class="board-3d-thumb-wrap">
                          <img src="${safeThumb}" alt="${safeName}" class="board-3d-thumb-img" loading="lazy">
                        </div>
                      ` : ''}
                      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px; gap: 8px;">
                        <span style="font-weight: 800; font-size: 0.95rem; color: #fff; line-height: 1.25;">${safeName}</span>
                        <div style="display: flex; gap: 4px; flex-shrink: 0;">
                          <span class="board-3d-badge-builtin">OFFICIAL</span>
                          ${isSelected ? '<span class="board-3d-badge-active">ACTIVE</span>' : ''}
                        </div>
                      </div>
                      <p style="font-size: 0.78rem; color: #94a3b8; margin: 0 0 10px 0; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">${safeDesc}</p>
                    </div>

                    <div>
                      <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.75rem; color: #cbd5e1; margin-bottom: 4px;">
                        <span>Tracks: <strong>${Number(b.trackCount) || 3}</strong></span>
                        <span>Target: <strong>${Number(b.target) || 121}</strong></span>
                      </div>
                      <div style="margin-top: 6px;">
                        <button type="button" class="btn-board-action btn-board-select" data-action="select" data-id="${safeId}" style="width: 100%; padding: 4px 8px; border-radius: 6px; font-size: 0.78rem; font-weight: 700; background: ${isSelected ? 'var(--gold, #f0c64a)' : 'rgba(240,198,74,0.18)'}; color: ${isSelected ? '#000' : 'var(--gold, #f0c64a)'}; border: 1px solid var(--gold, #f0c64a); cursor: pointer;">${isSelected ? '✓ In Use' : '▶️ Select Board'}</button>
                      </div>
                    </div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>

          <div class="modal-footer" style="padding: 12px 22px; display: flex; justify-content: flex-end; gap: 10px; border-top: 1px solid rgba(240, 198, 74, 0.2); background: rgba(0,0,0,0.35);">
            <button type="button" class="btn-ghost" id="btnBoard3DCancel" style="padding: 8px 20px; border-radius: 8px; cursor: pointer; font-weight: 700; color: #f4ead4; border: 1px solid rgba(255,255,255,0.2);">Close</button>
          </div>
        </div>
      `;

      bindEvents();
    };

    const closeModal = () => {
      modalEl.style.display = 'none';
      const fsElem = (typeof document !== 'undefined')
        ? (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement)
        : null;
      const b3dElem = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
        ? document.querySelector('.board-3d-container.is-fullscreen, .board-3d-container:fullscreen')
        : null;
      if (modalEl.parentElement !== document.body && !fsElem && !b3dElem && document.body) {
        document.body.appendChild(modalEl);
      }
    };

    const bindEvents = () => {
      modalEl.onclick = (e) => {
        if (e.target === modalEl) closeModal();
      };

      if (!window._board3DEscapeBound && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window._board3DEscapeBound = true;
        window.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') {
            const el = document.getElementById('board3DModal');
            if (el && el.style.display !== 'none') {
              el.style.display = 'none';
            }
          }
        });
      }

      const btnClose = modalEl.querySelector('#btnBoard3DClose');
      if (btnClose) btnClose.onclick = closeModal;

      const btnCancel = modalEl.querySelector('#btnBoard3DCancel');
      if (btnCancel) btnCancel.onclick = closeModal;

      // Image error handling for thumbnail fallbacks
      modalEl.querySelectorAll('img.board-3d-thumb-img').forEach(img => {
        img.addEventListener('error', () => {
          const wrap = img.closest('.board-3d-thumb-wrap');
          if (wrap) wrap.style.display = 'none';
        }, { once: true });
      });

      // Brand tabs click
      modalEl.querySelectorAll('.board-brand-tab-btn').forEach(btn => {
        btn.onclick = () => {
          currentBrandTab = btn.getAttribute('data-brand-tab') || 'all';
          renderModal();
        };
      });

      // View Mode Toggles
      const btnMode2D = modalEl.querySelector('#btnMode2D');
      if (btnMode2D) {
        btnMode2D.onclick = () => {
          settings.boardViewMode = '2d';
          const elBv = document.getElementById('optBoardView');
          if (elBv) elBv.value = '2d';
          SettingsManager.saveSettingsStore(settings);
          onSelect({ viewMode: '2d', boardId: settings.selected3DBoardId });
          closeModal();
        };
      }

      const btnMode3D = modalEl.querySelector('#btnMode3D');
      if (btnMode3D) {
        btnMode3D.onclick = () => {
          settings.boardViewMode = '3d';
          const elBv = document.getElementById('optBoardView');
          if (elBv) elBv.value = '3d';
          SettingsManager.saveSettingsStore(settings);
          onSelect({ viewMode: '3d', boardId: settings.selected3DBoardId });
          closeModal();
        };
      }

      const selectBoard = (boardId) => {
        settings.selected3DBoardId = boardId;
        const activeViewMode = settings.boardViewMode || '3d';
        const el3d = document.getElementById('opt3DBoard');
        if (el3d) el3d.value = boardId;
        const elInGame = document.getElementById('inGameOpt3DBoard');
        if (elInGame) elInGame.value = boardId;
        SettingsManager.saveSettingsStore(settings);
        onSelect({ viewMode: activeViewMode, boardId });
        closeModal();
      };

      // Board Cards Click (Select Board)
      modalEl.querySelectorAll('.board-3d-card').forEach(card => {
        card.onclick = (e) => {
          if (e.target.closest('.btn-board-action')) return;
          const boardId = card.getAttribute('data-board-id');
          if (boardId) selectBoard(boardId);
        };
      });

      // Card Action Buttons (Select)
      modalEl.querySelectorAll('.btn-board-action').forEach(btn => {
        btn.onclick = (e) => {
          e.stopPropagation();
          const action = btn.getAttribute('data-action');
          const boardId = btn.getAttribute('data-id');
          if (action === 'select' && boardId) {
            selectBoard(boardId);
          }
        };
      });
    };

    renderModal();
    modalEl.style.display = 'flex';
  }
}
